import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, ResponsiveContainer } from 'recharts';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import MIHeader from './MIHeader';
import VisitorCounter from './VisitorCounter';
import FortnightNotice from './FortnightNotice';
import { fmtMT, fmtMonth, fmtMonthShort, fmtNum, pctChange, shiftYear, slugify, getFYStart, getFYPlainLabel, fetchAllPaginated, fetchFortnightFYByMill, fetchLatestFortnightBreakdown, formatFortnightRange } from './utils';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../../hooks/useTranslation';
import { localizedFactoryName, localizedStateName, localizedDistrictName } from '../../utils/localizedDisplayName';

const ShareIcon = ({ size = 16, color = '#F59E0B' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" style={{ flexShrink: 0 }}>
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.6" y1="10.5" x2="15.4" y2="6.5" /><line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
  </svg>
);

const localeByLanguage = {
  en: 'en-IN',
  hi: 'hi-IN',
  te: 'te-IN',
  ta: 'ta-IN',
  kn: 'kn-IN',
  mr: 'mr-IN',
  gu: 'gu-IN',
};

const GovtSugarQuotaTracker = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latest, setLatest] = useState(null);
  const [prevMonth, setPrevMonth] = useState(null);
  const [prevYear, setPrevYear] = useState(null);
  const [trend, setTrend] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [fyLabel, setFyLabel] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [visibleCount, setVisibleCount] = useState(12);
  const [stateCount, setStateCount] = useState(0);
  const [districtCount, setDistrictCount] = useState(0);
  const [millCount, setMillCount] = useState(0);
  const [fortnight, setFortnight] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: monthly, error: monthlyErr } = await supabase
          .from('mi_v_national_monthly')
          .select('period_month, total_allocated_mt')
          .order('period_month', { ascending: false })
          .limit(13);
        if (monthlyErr) throw monthlyErr;
        if (!monthly || monthly.length === 0) throw new Error('No release order data available yet');

        const sorted = [...monthly].reverse();
        const latestRow = sorted[sorted.length - 1];
        const prevMonthRow = sorted.length > 1 ? sorted[sorted.length - 2] : null;
        const fyStart = getFYStart(latestRow.period_month);

        // These 5 only depend on latestRow.period_month (already known above),
        // not on each other - fire together instead of one after another
        // (was 5 sequential round trips, now 1).
        const [
          { data: yoyRow },
          quotaRows,
          fyRows,
          { count: mCount },
          fortnightFY,
          fortnightBreakdown,
        ] = await Promise.all([
          supabase.from('mi_v_national_monthly').select('period_month, total_allocated_mt').eq('period_month', shiftYear(latestRow.period_month, -1)).maybeSingle(),
          fetchAllPaginated(() =>
            supabase
              .from('mi_v_release_orders_canonical')
              .select('mill_id, mill_code_raw, canonical_mill_name, canonical_state, canonical_district, allocated_qty_mt')
              .eq('period_month', latestRow.period_month)
              .not('canonical_mill_name', 'eq', '')
              .order('allocated_qty_mt', { ascending: false })
          ),
          fetchAllPaginated(() =>
            supabase
              .from('mi_v_release_orders_canonical')
              .select('mill_id, allocated_qty_mt')
              .gte('period_month', fyStart)
              .lte('period_month', latestRow.period_month)
              .not('mill_id', 'is', null)
          ),
          supabase.from('mi_mills').select('id', { count: 'exact', head: true }),
          fetchFortnightFYByMill(latestRow.period_month),
          fetchLatestFortnightBreakdown(),
        ]);

        const fyTotals = {};
        fyRows.forEach((r) => { fyTotals[r.mill_id] = (fyTotals[r.mill_id] || 0) + Number(r.allocated_qty_mt); });
        fortnightFY.forEach((qty, millId) => { fyTotals[millId] = (fyTotals[millId] || 0) + qty; });
        const quotaRowsWithFY = quotaRows.map((r) => ({
          ...r,
          fyCumulative: r.mill_id ? (fyTotals[r.mill_id] || 0) : null,
          fortnightMt: r.mill_id && fortnightBreakdown ? (fortnightBreakdown.byMill.get(r.mill_id) ?? 0) : null,
        }));
        if (fortnightBreakdown) quotaRowsWithFY.sort((a, b) => (b.fortnightMt ?? 0) - (a.fortnightMt ?? 0));

        if (!isMounted) return;
        setLatest(latestRow);
        setPrevMonth(prevMonthRow);
        setPrevYear(yoyRow || null);
        setAllRows(quotaRowsWithFY);
        setFyLabel(getFYPlainLabel(latestRow.period_month));
        setFortnight(fortnightBreakdown);
        setTrend(sorted.map((r) => ({ month: new Date(r.period_month).toLocaleDateString('en-IN', { month: 'short' }), mt: Number(r.total_allocated_mt) })));
        setMillCount(mCount || 0);
        setStateCount(new Set(quotaRowsWithFY.map((r) => r.canonical_state).filter(Boolean)).size);
        setDistrictCount(new Set(quotaRowsWithFY.map((r) => r.canonical_district).filter(Boolean)).size);
      } catch (e) {
        if (isMounted) setError(e.message || 'Failed to load data');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, []);

  const momPct = latest && prevMonth ? pctChange(latest.total_allocated_mt, prevMonth.total_allocated_mt) : null;
  const yoyPct = latest && prevYear ? pctChange(latest.total_allocated_mt, prevYear.total_allocated_mt) : null;

  const allStates = [...new Set(allRows.map((r) => r.canonical_state))].filter(Boolean).sort();
  const selectedStateCanonical = allStates.find((s) => slugify(s) === stateFilter);
  const districtsForState = stateFilter
    ? [...new Set(allRows.filter((r) => slugify(r.canonical_state) === stateFilter).map((r) => r.canonical_district))].filter(Boolean).sort()
    : [];
  let filteredRows = allRows;
  if (stateFilter) filteredRows = filteredRows.filter((r) => slugify(r.canonical_state) === stateFilter);
  if (districtFilter) filteredRows = filteredRows.filter((r) => slugify(r.canonical_district) === districtFilter);
  const shownRows = filteredRows.slice(0, visibleCount);

  const handleStateChange = (e) => { setStateFilter(e.target.value); setDistrictFilter(''); setVisibleCount(12); };
  const handleDistrictChange = (e) => { setDistrictFilter(e.target.value); setVisibleCount(12); };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Helmet>
        <title>{t('marketIntelligence.homeSeoTitle')}</title>
        <meta name="description" content={t('marketIntelligence.homeSeoDescription')} />
      </Helmet>
      <MIHeader />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl py-8">
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 text-[var(--text-muted)]">
            <Icon name="Loader" size={32} className="animate-spin mb-3" /><p>{t('common.loading', 'Loading...')}</p>
          </div>
        )}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-[var(--text-muted)]">
            <Icon name="AlertTriangle" size={32} className="mb-3 text-[var(--error)]" /><p>{t('marketIntelligence.loadError', { error })}</p>
          </div>
        )}

        {!loading && !error && latest && (
          <>
            <h1 className="text-2xl sm:text-3xl font-black text-[var(--text-primary)] mb-1">{t('mandiFactory.govtQuotaTracker', 'Govt Sugar Quota Tracker')}</h1>

            <FortnightNotice />

            {/* TABLE FIRST */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs text-[var(--text-muted)] block mb-1.5">{t('marketplace.state', 'State')}</label>
                <select aria-label={t('marketplace.state', 'State')} value={stateFilter} onChange={handleStateChange} className="w-full bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] rounded-[var(--radius-input)] px-3 py-2.5 text-sm font-medium">
                  <option value="" style={{ background: 'var(--bg-primary)' }}>{t('marketplace.allStates', 'All states')}</option>
                  {allStates.map((s) => <option key={s} value={slugify(s)} style={{ background: 'var(--bg-primary)' }}>{localizedStateName(s, language)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)] block mb-1.5">{t('mandi.selectDistrict', 'District')}</label>
                <select aria-label={t('mandi.selectDistrict', 'District')} value={districtFilter} onChange={handleDistrictChange} disabled={!stateFilter || loading} className="w-full bg-[var(--bg-card)] border border-[var(--border-primary)] text-[var(--text-primary)] rounded-[var(--radius-input)] px-3 py-2.5 text-sm font-medium disabled:opacity-40">
                  <option value="" style={{ background: 'var(--bg-primary)' }}>{t('marketIntelligence.allDistricts', 'All districts')}</option>
                  {districtsForState.map((d) => <option key={d} value={slugify(d)} style={{ background: 'var(--bg-primary)' }}>{localizedDistrictName(d, selectedStateCanonical, language)}</option>)}
                </select>
              </div>
            </div>

            <button onClick={() => navigate('/market-intelligence/search')} className="w-full rounded-[var(--radius-card)] px-4 py-2.5 text-center transition-opacity hover:opacity-90 mb-4" style={{ background: 'var(--mills-card)', border: '1px solid #5c4a30' }}>
              <p className="text-sm font-medium" style={{ color: '#FBBF24' }}>{t('marketIntelligence.searchFactoryPlaceholder')} &rarr;</p>
            </button>

            <button onClick={() => navigate('/market-intelligence/statewise-allocation')} className="w-full flex items-center justify-between rounded-[var(--radius-input)] px-3 py-2.5 mb-4 transition-opacity hover:opacity-90" style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)' }}>
              <span className="text-xs font-semibold" style={{ color: 'var(--primary)' }}>{t('marketIntelligence.downloadStatewiseAllocation')}</span>
              <span className="text-xs font-semibold" style={{ color: 'var(--primary)' }}>&rarr;</span>
            </button>

            <div className="rounded-[var(--radius-card)] mb-2" style={{ border: '1px solid var(--border-primary)', boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ overflowX: 'auto', borderRadius: 'var(--radius-card)' }}>
                <table className="text-sm" style={{ tableLayout: 'fixed', width: fortnight ? '700px' : '580px', borderCollapse: 'collapse' }}>
                  <colgroup>
                    <col style={{ width: '28px' }} />
                    <col style={{ width: '60px' }} />
                    <col style={{ width: '210px' }} />
                    <col style={{ width: '150px' }} />
                    {fortnight && <col style={{ width: '130px' }} />}
                    <col style={{ width: '120px' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ background: 'var(--bg-secondary)' }}>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-[var(--text-muted)]" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>#</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-[var(--text-muted)]" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{t('marketIntelligence.code', 'Code')}</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-[var(--text-muted)]" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{t('common.factory', 'Factory')}</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-[var(--text-muted)]" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{t('marketIntelligence.districtState', 'District / State')}</th>
                      {fortnight && (
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-[var(--text-muted)]" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {t('marketIntelligence.fortnightColumnLabel', { range: formatFortnightRange(fortnight.periodStart, fortnight.periodEnd, language) || fortnight.periodLabel, defaultValue: '{{range}} (MT)' })}
                        </th>
                      )}
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-[var(--text-muted)]" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{fyLabel} (MT)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownRows.map((r, i) => {
                      const isCream = i % 2 === 1;
                      const factoryDisplayName = localizedFactoryName(r, language, 'canonical_mill_name');
                      return (
                        <tr key={i} style={{ background: isCream ? '#FDF6E9' : (i % 4 === 0 ? '#24364F' : '#0B1626'), borderTop: '1px solid var(--border-light)' }}>
                          <td className="px-3 py-2.5 align-top" style={{ color: isCream ? '#57534E' : '#D8DEE8' }}>{i + 1}</td>
                          <td className="px-3 py-2.5 align-top" style={{ color: isCream ? '#57534E' : '#D8DEE8' }}>{r.mill_code_raw}</td>
                          <td className="px-3 py-2.5 align-top">
                            <button onClick={() => navigate(`/market-intelligence/factory/${slugify(r.canonical_mill_name)}`)} className="flex items-start gap-2 text-left w-full min-w-0">
                              <span className="min-w-0 flex-1" style={{ color: isCream ? '#1C1917' : '#FFFFFF', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{factoryDisplayName}</span>
                              <ShareIcon size={15} color={isCream ? '#D97706' : '#F59E0B'} />
                            </button>
                          </td>
                          <td className="px-3 py-2.5 align-top">
                            <button onClick={() => navigate(`/market-intelligence/district/${slugify(r.canonical_district)}`)} className="flex items-start gap-1.5 text-left w-full min-w-0">
                              <span className="min-w-0 flex-1" style={{ color: isCream ? '#57534E' : '#D8DEE8', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textDecoration: 'underline', textDecorationColor: isCream ? '#D6D3D1' : '#3F506B', textUnderlineOffset: '2px' }}>{localizedDistrictName(r.canonical_district, r.canonical_state, language)}, {localizedStateName(r.canonical_state, language)}</span>
                              <ShareIcon size={13} color={isCream ? '#D97706' : '#F59E0B'} />
                            </button>
                          </td>
                          {fortnight && (
                            <td className="px-3 py-2.5 align-top text-left font-bold" style={{ color: isCream ? '#B45309' : '#F59E0B' }}>{r.fortnightMt !== null ? fmtNum(r.fortnightMt) : 'N/A'}</td>
                          )}
                          <td className="px-3 py-2.5 align-top text-left" style={{ color: isCream ? '#57534E' : '#D8DEE8' }}>{r.fyCumulative !== null ? fmtNum(r.fyCumulative) : 'N/A'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>&larr; {t('marketIntelligence.scrollHint', 'Scroll horizontally to see all columns on narrow screens')}</p>
            <div className="flex items-center justify-between mb-8">
              <p className="text-xs text-[var(--text-muted)]">{filteredRows.length} factories match this filter</p>
              <div className="flex gap-2">
                {visibleCount < filteredRows.length && (
                  <button aria-label={t('marketIntelligence.loadMoreRemaining', { count: filteredRows.length - visibleCount })} onClick={() => setVisibleCount((v) => v + 20)} className="text-xs font-semibold px-3 py-1.5 rounded-[var(--radius-input)]" style={{ background: 'rgba(245,158,11,0.12)', color: 'var(--primary)' }}>{t('marketIntelligence.loadMoreRemaining', { count: filteredRows.length - visibleCount })}</button>
                )}
                <button aria-label={t('marketIntelligence.allFactories')} onClick={() => navigate('/market-intelligence/factories')} className="text-xs font-semibold px-3 py-1.5 rounded-[var(--radius-input)]" style={{ background: 'rgba(59,130,246,0.12)', color: 'var(--info)' }}>{t('marketIntelligence.allFactories')} &rarr;</button>
              </div>
            </div>

            <div className="h-px bg-[var(--border-light)] mb-6" />

            {/* BLOCKS - wow backgrounds */}
            <p className="text-sm font-semibold text-[var(--text-primary)] mb-2">{t('marketIntelligence.browseBy', 'Browse by')} <span className="text-xs font-normal text-[var(--text-muted)]">&middot; {t('marketIntelligence.dataFor', 'data for {{month}}', { month: fmtMonthShort(latest.period_month) })}</span></p>
            <div className="grid grid-cols-3 gap-3 mb-8">
              <button onClick={() => navigate('/market-intelligence/states')} className="rounded-[var(--radius-card)] p-4 text-left transition-transform hover:scale-[1.02] relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #F59E0B 0%, #D97706 100%)', boxShadow: '0 8px 20px rgba(245,158,11,0.25)' }}>
                <Icon name="Map" size={20} style={{ color: '#78350F' }} className="mb-2" />
                <p className="text-lg font-black" style={{ color: '#0F2238' }}>{stateCount}</p>
                <p className="text-xs font-semibold" style={{ color: '#78350F' }}>{t('marketplace.allStates', 'States')}</p>
              </button>
              <button onClick={() => navigate('/market-intelligence/districts')} className="rounded-[var(--radius-card)] p-4 text-left transition-transform hover:scale-[1.02]" style={{ background: 'var(--trade-card)', border: '1px solid #1d6873' }}>
                <Icon name="MapPin" size={20} style={{ color: '#5EEAD4' }} className="mb-2" />
                <p className="text-lg font-black text-[var(--text-primary)]">{districtCount}</p>
                <p className="text-xs" style={{ color: '#5EEAD4' }}>{t('marketIntelligence.districtsNav')}</p>
              </button>
              <button onClick={() => navigate('/market-intelligence/factories')} className="rounded-[var(--radius-card)] p-4 text-left transition-transform hover:scale-[1.02]" style={{ background: 'var(--transport-card)', border: '1px solid #2c6690' }}>
                <Icon name="Factory" size={20} style={{ color: '#93C5FD' }} className="mb-2" />
                <p className="text-lg font-black text-[var(--text-primary)]">{millCount}</p>
                <p className="text-xs" style={{ color: '#93C5FD' }}>{t('marketIntelligence.allFactories')}</p>
              </button>
            </div>

            <div className="rounded-[var(--radius-card)] p-5 mb-6" style={{ background: 'var(--payment-card)', border: '1px solid #4a4670', boxShadow: 'var(--shadow-sm)' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'var(--gradient-button)' }}><Icon name="Zap" size={14} className="text-[var(--bg-primary)]" /></div>
                <span className="text-sm font-semibold text-[var(--text-primary)]">{t('mandiFactory.summaryTitle')}</span>
              </div>
              <ul className="space-y-2">
                <li className="text-sm flex gap-2" style={{ color: '#C4B5FD' }}>
                  <span style={{ color: 'var(--primary)' }}>&bull;</span>
                  <span>{t('marketIntelligence.summaryNational', { month: fmtMonth(latest.period_month), total: fmtMT(latest.total_allocated_mt), mom: momPct === null ? '' : `${momPct >= 0 ? '+' : '-'}${Math.abs(momPct).toFixed(1)}%`, yoy: yoyPct === null ? '' : `${yoyPct >= 0 ? '+' : '-'}${Math.abs(yoyPct).toFixed(1)}%` })}</span>
                </li>
                <li className="text-sm flex gap-2" style={{ color: '#C4B5FD' }}>
                  <span style={{ color: 'var(--primary)' }}>&bull;</span>
                  <span>{t('marketIntelligence.summaryTracking', { mills: millCount, states: stateCount, districts: districtCount })}</span>
                </li>
              </ul>
            </div>

            {trend.length > 1 && (
              <div className="rounded-[var(--radius-card)] p-5 mb-6" style={{ background: 'var(--trade-card)', border: '1px solid #1d6873' }}>
                <p className="text-sm font-semibold text-[var(--text-primary)] mb-2">{t('marketIntelligence.nationalTrend', 'National trend, last 12 months')}</p>
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trend}>
                      <XAxis dataKey="month" stroke="#5EEAD4" fontSize={10} tickLine={false} axisLine={false} />
                      <Line type="monotone" dataKey="mt" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3, fill: '#F59E0B' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <VisitorCounter />
          </>
        )}
      </main>
    </div>
  );
};

export default GovtSugarQuotaTracker;
